import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import fordStyles from "./BrandSettings-Ford.module.scss";
import lincolnStyles from "./BrandSettings-Lincoln.module.scss";
import Item from "../../../shared/item/Item";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Typography, Button, Grid, Radio, Box } from "@mui/material";
import ValidationRules from "../../../common/ValidationRules";
import { DNP, SRP } from "../PriceType";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import BrandSettingsClient from "../../../clients/BrandSettingsClient";
import { logMsg } from "../../../common/Logger";
import HasPermissionTo from "../../../common/HasPermissionTo";
import {
  displayError,
  ERROR_PROCESSING,
  is404,
  isProcessingError,
  PAGE_ERROR,
} from "../../../errors/ErrorConstants";
import { BRAND_NAMES } from "../../../common/Constants";
import TitleCase from "../../../utils/TitleCase";
import CapitalizeEachWord from "../../../utils/CapitalizeEachWord";
import * as AnalyticsService from "../../../shared/services/analytics/AnalyticsService";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPValidatedInputField from "../../../shared/components/EMPValidatedInputField/EMPValidatedInputField";
import EMPDialog from "../../../shared/components/EMPDialog/EMPDialog";

let isFordBrand = true;
let styles = null;

const deepCloneFallback = (value) => {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepCloneFallback(item));
  }
  const clonedObject = {};
  Object.keys(value).forEach((key) => {
    clonedObject[key] = deepCloneFallback(value[key]);
  });
  return clonedObject;
};

const cloneSettings = (value) => {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return deepCloneFallback(value);
};

const BrandSettings = (props) => {
  const [brandSettings, setBrandSettings] = useState(() =>
    props.user?.dealerGroup?.length === 0
      ? { priceType: SRP, markup: "", laborRate: "" }
      : null
  );
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [overridesApplied, setOverridesApplied] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [hasError, setHasError] = useState([]);
  const [formHasErrors, setFormHasErrors] = useState(false);

  const formRef = useRef(null);
  const previousDataRef = useRef({});
  const confirmDialogPropsRef = useRef({});

  const enableOrDisableButton = (nextBrandSettings) => {
    const previousData = previousDataRef.current;
    if (!previousData || !nextBrandSettings) {
      setButtonEnabled(false);
      return;
    }
    const hasValidMarkup =
      nextBrandSettings.markup === "" ||
      /^\d+$/.test(String(nextBrandSettings.markup));
    if (!hasValidMarkup) {
      setButtonEnabled(false);
      return;
    }
    const isPriceTypeChanged = previousData.priceType !== nextBrandSettings.priceType;
    const isMarkupChanged =
      nextBrandSettings.markup !== "" &&
      previousData.markup !== Number.parseFloat(nextBrandSettings.markup);
    const isLaborRateChanged =
      nextBrandSettings.laborRate !== "" &&
      previousData.laborRate !== Number.parseFloat(nextBrandSettings.laborRate);

    setButtonEnabled(
      Boolean(isPriceTypeChanged || isMarkupChanged || isLaborRateChanged)
    );
  };

  const getDealerBrandAdjustments = () => {
    isFordBrand = IS_FORD_BRAND(props.user.brand);
    styles = isFordBrand ? fordStyles : lincolnStyles;
    props.hideOrShow(true);
    let loadedBrandSettings;
    let enableTabs = false;

    Promise.resolve(BrandSettingsClient.getDealerBrandAdjustments(props.user))
      .then((data) => {
        const normalizedData = data || {};
        enableTabs = !!normalizedData.priceType;
        if (!enableTabs) {
          normalizedData.priceType = SRP;
        }
        loadedBrandSettings = normalizedData;

        confirmDialogPropsRef.current = {
          title: props.t("BrandSettings.dialog.confirm.title"),
          bodyText: props.t("BrandSettings.dialog.confirm.body"),
          settings: props.t("BrandSettings.dialog.confirm.settings", {
            markup: loadedBrandSettings.markup,
            labourRate: loadedBrandSettings.laborRate,
          }),
          markup: props.t("BrandSettings.dialog.confirm.markup"),
          laborRate: props.t("BrandSettings.dialog.confirm.laborRate"),
          checkbox: props.t("BrandSettings.dialog.confirm.checkbox"),
          confirm: isFordBrand
            ? props.t("BrandSettings.dialog.confirm.F.confirmPublish")
            : props.t("BrandSettings.dialog.confirm.L.confirm"),
          cancel: isFordBrand
            ? props.t("BrandSettings.dialog.confirm.F.goBack")
            : props.t("BrandSettings.dialog.confirm.L.cancel"),
        };

        previousDataRef.current = cloneSettings(loadedBrandSettings);
      })
      .catch((error) => {
        logMsg("reason", error);
        if (is404(error)) {
          setHasError([PAGE_ERROR]);
        } else if (isProcessingError(error)) {
          setHasError([ERROR_PROCESSING]);
        }
      })
      .finally(() => {
        setBrandSettings(
          loadedBrandSettings || { priceType: SRP, markup: "", laborRate: "" }
        );
        setButtonEnabled(false);
        props.enableOtherTabs(enableTabs);
        props.hideOrShow(false);
      });
  };

  useEffect(() => {
    AnalyticsService.trackPageLoadEvent("fv:emp:accessories pricing:brand", props.user);
    ValidationRules();
    getDealerBrandAdjustments();
  }, []);

  const handleOptionChange = (value) => {
    const copy = cloneSettings(brandSettings);
    copy.priceType = value;
    setBrandSettings(copy);
    enableOrDisableButton(copy);
  };

  const handleClose = () => {
    setConfirmDialog(false);
  };

  const handleConfirm = (event) => {
    if (event) {
      event.preventDefault();
    }
    setConfirmDialog(true);
  };

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }
    props.hideOrShow(true);
    const data = cloneSettings(brandSettings);
    data.markup = Number.parseFloat(data.markup);
    data.laborRate = Number.parseFloat(data.laborRate);
    data.salesCode = props.user.salesCode;

    BrandSettingsClient.saveDealerBrandAdjustments(
      props.user,
      data,
      props.user.market === "CAN" ? "CAN" + props.user.dealerId : props.user.dealerId,
      props.user.brand
    )
      .then(() => {
        setButtonEnabled(false);
        setConfirmDialog(false);
        setOverridesApplied(true);
        props.enableOtherTabs(true);
        previousDataRef.current = data;
      })
      .catch((error) => {
        logMsg("the error is: ", error);
        if (is404(error)) {
          setHasError([PAGE_ERROR]);
        } else if (isProcessingError(error)) {
          setHasError([ERROR_PROCESSING]);
        }
      })
      .finally(() => props.hideOrShow(false));
  };

  const updateModel = (event, model, field) => {
    const copy = cloneSettings(brandSettings);
    if (field === "markup") {
      copy.markup = event.target.value;
    } else {
      copy.laborRate = event.target.value;
    }

    setBrandSettings(copy);
    enableOrDisableButton(copy);

    if (formRef.current?.isFormValid) {
      formRef.current.isFormValid(false).then((valid) => {
        if (formHasErrors === !valid) return;
        setFormHasErrors(!valid);
      });
    }

    confirmDialogPropsRef.current.settings = props.t(
      "BrandSettings.dialog.confirm.settings",
      {
        markup: copy.markup,
        labourRate: copy.laborRate,
      }
    );
  };

  if (displayError(hasError)) {
    throw new Error(hasError[0]);
  }

  props.user.showWarning(buttonEnabled);
  return (
    brandSettings && (
      <div className={styles.pagePadding}>
        <ValidatorForm
          ref={formRef}
          onSubmit={(event) => handleConfirm(event)}
          onError={(errors) => {
            const hasErrors = errors && errors.length > 0;
            if (hasErrors !== formHasErrors) {
              setFormHasErrors(hasErrors);
            }
          }}
          instantValidate
        >
          <Typography variant="h1" sx={{ my: 4 }}>
            {props.t("BrandSettings.header.pricing")}
          </Typography>
          <Typography variant="body1">{props.t("BrandSettings.title.pricing")}</Typography>

          <PriceType
            {...props}
            onClick={handleOptionChange}
            selectedOption={brandSettings?.priceType}
          />

          <BrandMarkup
            {...props}
            updateModel={updateModel}
            brandSettings={brandSettings}
          />
          <SaveButton
            {...props}
            enabled={buttonEnabled && !formHasErrors}
            isFordBrand={isFordBrand}
          />
        </ValidatorForm>

        <OverridesDialog
          {...props}
          overridesApplied={overridesApplied}
          handleTabChange={props.handleTabChange}
          onClose={() => setOverridesApplied(false)}
        />

        <ConfirmDialog
          open={confirmDialog}
          showSettings={true}
          showWarningIcon={true}
          dialogProps={confirmDialogPropsRef.current}
          markupValue={brandSettings ? brandSettings.markup : null}
          laborRateValue={brandSettings ? brandSettings.laborRate : null}
          onConfirm={handleSubmit}
          onCancel={handleClose}
          boldTitle={true}
          onClose={handleClose}
          disableConfirm={
            !props.user.permissions.rolePermissions.hasOwnProperty("accessoriesSubmit")
          }
          {...props}
        />
      </div>
    )
  );
};

const SaveButton = (props) => {
  return (
    <Box sx={{ mt: 6, textAlign: "right", mb: 10 }}>
      <EMPButton
        disabled={!props.enabled}
        testId="applyOverrides"
        variant="contained"
        submitButton
      >
        {props.t("BrandSettings.save")}
      </EMPButton>
    </Box>
  );
};

const PriceType = (props) => {
  const handleSelection = (radio) => {
    props.onClick(radio);
  };

  const handleSelectionKeyDown = (event, radio) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelection(radio);
    }
  };

  function getRadio(radio) {
    const isLincolnChecked = !isFordBrand && props.selectedOption === radio;
    return (
      <div
        className={isLincolnChecked ? styles.radioChecked : styles.radio}
        role="radio"
        aria-checked={props.selectedOption === radio}
        tabIndex={0}
        onClick={() => handleSelection(radio)}
        onTouchStart={() => handleSelection(radio)}
        onKeyDown={(event) => handleSelectionKeyDown(event, radio)}
      >
        <div>
          <Radio
            value={radio}
            name={radio}
            data-testid={radio}
            checked={props.selectedOption === radio}
            onChange={() => {}}
          />
        </div>
        <div className={styles.text}>
          <div className={styles.radioTitle}>
            {" "}
            {props.t("BrandSettings." + radio)}
          </div>
          <div className={styles.radioText}>
            {props.t("BrandSettings.text." + radio, {
              brand: isFordBrand
                ? TitleCase(BRAND_NAMES.FORD)
                : TitleCase(BRAND_NAMES.LINCOLN),
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.priceType}>
      {getRadio(SRP)}
      {getRadio(DNP)}
    </div>
  );
};

const BrandMarkup = (props) => {
  const model = props.brandSettings;
  return (
    <>
      <Typography variant="h3" sx={{ my: 4 }}>
        {props.t("BrandSettings.header.markup")}
      </Typography>
      <Typography variant="body1" sx={{ mt: 4, mb: 6 }}>
        {props.t("BrandSettings.title.markup")}
      </Typography>

      <div className={`${styles.brandHeader}`}>
        <Grid container spacing={0} justifyContent="space-between">
          <Grid container item xs={6}>
            <Grid item>
              <Item value={props.t("BrandSettings.brand")} />
            </Grid>
          </Grid>
          <Grid container item xs={6} spacing={4}>
            <Grid item xs={6}>
              <Item value={props.t("BrandSettings.markup")} />
            </Grid>
            <Grid item xs={6}>
              <Item value={props.t("BrandSettings.laborRate")} />
            </Grid>
          </Grid>
        </Grid>
      </div>

      <Grid
        container
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ paddingTop: 4 }}
      >
        <Grid container item xs={6}>
          <Grid item>
            <Typography variant="body1">
              {props.t("BrandSettings.model." + props.user.brand)}
            </Typography>
          </Grid>
        </Grid>
        <Grid container item xs={6} spacing={4}>
          <Grid item xs={6}>
            <EMPValidatedInputField
              value={model.markup || model.markup === 0 ? model.markup : ""}
              testid="markup"
              name="markup"
              validators={["required", "isNumeric"]}
              errorMessages={[
                props.t("Error.requiredField"),
                props.t("Error.invalidInput"),
              ]}
              onChange={(e) => props.updateModel(e, model, "markup")}
              endAdornment="% "
            />
          </Grid>
          <Grid item xs={6}>
            <EMPValidatedInputField
              value={
                model.laborRate || model.laborRate === 0 ? model.laborRate : ""
              }
              testid="laborRate"
              name="laborRate"
              startAdornment="$ "
              validators={[
                "required",
                String.raw`matchRegexp:^[0-9\b.]+$`,
                "isNumber",
                "isNumericWithTwoDecimalPlaces",
              ]}
              errorMessages={[
                props.t("Error.requiredField"),
                props.t("Error.invalidInput"),
                props.t("Error.invalidInput"),
                props.t("Error.invalidInput"),
              ]}
              onChange={(e) => props.updateModel(e, model, "laborRate")}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

const OverridesDialog = (props) => {
  const handleTabChange = props.handleTabChange || (() => {});
  return (
    <EMPDialog
      open={props.overridesApplied}
      handleClose={props.onClose}
      hasCloseIcon
      dialogTitle={props.t("BrandSettings.dialog.overrides.title")}
      hasActionButton
      actionButtonText={props.t("BrandSettings.dialog.overrides.done")}
      onConfirm={props.onClose}
    >
      <div>
        <Typography variant="body1">
          {props.t("BrandSettings.dialog.overrides.description")}
        </Typography>
        <Grid id="overridesDialog">
          <Grid item md={12}>
            <div className={styles.buttons}>
              <HasPermissionTo
                perform={["namePlateSettingsTabUS"]}
                permissions={props.user.permissions.rolePermissions}
                render={() => {
                  return (
                    <Button
                      className={styles.customiseToBtn}
                      onClick={(event) => handleTabChange(event, 1)}
                    >
                      <div
                        className={props.isFordBrand ? "" : styles.lincolnBtn}
                      >
                        {isFordBrand ? (
                          props.t(
                            "BrandSettings.dialog.overrides.nameplatesBtn"
                          )
                        ) : (
                          <>
                            {CapitalizeEachWord(
                              props.t(
                                "BrandSettings.dialog.overrides.nameplatesBtn"
                              )
                            )}
                            <span className={styles.chevronOffset}></span>
                          </>
                        )}
                      </div>
                    </Button>
                  );
                }}
              />
              <HasPermissionTo
                perform={["categorySettingsTabUS"]}
                permissions={props.user.permissions.rolePermissions}
                render={() => {
                  return (
                    <Button
                      className={styles.customiseToBtn}
                      onClick={(event) => handleTabChange(event, 2)}
                    >
                      <div
                        className={props.isFordBrand ? "" : styles.lincolnBtn}
                      >
                        {isFordBrand ? (
                          props.t(
                            "BrandSettings.dialog.overrides.categoriesBtn"
                          )
                        ) : (
                          <>
                            {CapitalizeEachWord(
                              props.t(
                                "BrandSettings.dialog.overrides.categoriesBtn"
                              )
                            )}
                            <span className={styles.chevronOffset}></span>
                          </>
                        )}
                      </div>
                    </Button>
                  );
                }}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </EMPDialog>
  );
};

const userShape = PropTypes.shape({
  brand: PropTypes.string,
  dealerGroup: PropTypes.array,
  dealerId: PropTypes.string,
  market: PropTypes.string,
  permissions: PropTypes.shape({
    rolePermissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  }),
  salesCode: PropTypes.string,
  showWarning: PropTypes.func,
  token: PropTypes.string,
});

BrandSettings.propTypes = {
  enableOtherTabs: PropTypes.func.isRequired,
  handleTabChange: PropTypes.func,
  hideOrShow: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  user: userShape.isRequired,
};

SaveButton.propTypes = {
  enabled: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

PriceType.propTypes = {
  onClick: PropTypes.func.isRequired,
  selectedOption: PropTypes.string,
  t: PropTypes.func.isRequired,
};

BrandMarkup.propTypes = {
  brandSettings: PropTypes.shape({
    laborRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    markup: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  t: PropTypes.func.isRequired,
  updateModel: PropTypes.func.isRequired,
  user: userShape.isRequired,
};

OverridesDialog.propTypes = {
  handleTabChange: PropTypes.func,
  isFordBrand: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  overridesApplied: PropTypes.bool,
  t: PropTypes.func.isRequired,
  user: userShape.isRequired,
};

export default withTranslation("emp")(BrandSettings);
